import { BACKEND_URL } from "./apiConfig";

export const PRODUCT_PLACEHOLDER_IMAGE = `${process.env.PUBLIC_URL ?? ""}/placeholder.png`;
const ABSOLUTE_URL_REGEX = /^[a-z][a-z\d+\-.]*:\/\//i;
const PROTOCOL_RELATIVE_URL_REGEX = /^\/\//;
const DATA_OR_BLOB_URL_REGEX = /^(data:|blob:)/i;
const CSS_UNSAFE_CHARS_REGEX = /[()'"]/g;

/**
* Get products image
*
* @param {string} image - URL of the image
* @returns {string} Full url of the image
*/
export const getProductImage = (image: string | null | undefined): string => {
    if (!isNonEmptyString(image)) return PRODUCT_PLACEHOLDER_IMAGE;

    const normalizedImage = normalizeImagePath(image);

    try {
        if (DATA_OR_BLOB_URL_REGEX.test(normalizedImage)) return normalizedImage;
        const absoluteUrl = toAbsoluteUrl(normalizedImage);
        return encodeCssUnsafeCharacters(absoluteUrl.toString());
    } catch {
        return PRODUCT_PLACEHOLDER_IMAGE;
    }
};

type ImageEntry = {
    url?: string | null;
} | null | undefined;

type ProductImageSource = {
    image?: string | null;
    images?: ImageEntry[] | null;
} | null | undefined;

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === "string" && value.trim().length > 0;

const normalizeImagePath = (path: string): string => {
    const normalized = path.trim().replace(/\\/g, "/");
    return normalized.length > 0 ? normalized : path.trim();
};

const toAbsoluteUrl = (rawPath: string): URL => {
    if (ABSOLUTE_URL_REGEX.test(rawPath)) return new URL(rawPath);

    if (PROTOCOL_RELATIVE_URL_REGEX.test(rawPath)) {
        return new URL(`https:${rawPath}`);
    }

    const pathWithLeadingSlash = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return new URL(pathWithLeadingSlash, `${BACKEND_URL}/`);
};

const encodeCssUnsafeCharacters = (url: string): string =>
    url.replace(CSS_UNSAFE_CHARS_REGEX, (char) =>
        `%${char.charCodeAt(0).toString(16).toUpperCase()}`
    );

const getLegacyProductImagePath = (source: ProductImageSource): string => {
    const legacyPath = source?.image;
    return isNonEmptyString(legacyPath) ? legacyPath : "";
};

const getAdditionalProductImagePaths = (source: ProductImageSource): string[] =>
    source?.images
        ?.map((img) => {
            const imageUrl = img?.url;
            return isNonEmptyString(imageUrl) ? imageUrl : null;
        })
        .filter((url): url is string => url !== null) ?? [];

export const getPrimaryProductImagePath = (source: ProductImageSource): string => {
    const firstGalleryImage = getAdditionalProductImagePaths(source)[0];
    if (isNonEmptyString(firstGalleryImage)) return firstGalleryImage;
    return getLegacyProductImagePath(source);
};

export const getFirstAdditionalProductImagePath = (source: ProductImageSource): string => {
    const firstImagePath = getAdditionalProductImagePaths(source)[0];
    return isNonEmptyString(firstImagePath) ? firstImagePath : "";
};

export const getPrimaryProductImage = (source: ProductImageSource): string =>
    getProductImage(getPrimaryProductImagePath(source));

export const getAllProductImages = (source: ProductImageSource): string[] => {
    const galleryImagePaths = getAdditionalProductImagePaths(source);
    const legacyImagePath = getLegacyProductImagePath(source);
    const imagePaths =
        galleryImagePaths.length > 0
            ? galleryImagePaths
            : isNonEmptyString(legacyImagePath)
                ? [legacyImagePath]
                : [];

    if (imagePaths.length > 0) {
        return Array.from(new Set(imagePaths.map((path) => getProductImage(path))));
    }

    return [PRODUCT_PLACEHOLDER_IMAGE];
};
